/**
 * Finance Calendar data types and constants for the Finance Calendar Widget.
 * Mirrors the GraphQL FinanceCalendarEvent type.
 */

export const FINANCE_CALENDAR_SCENARIOS = ['Guidance', 'MEC', 'OP'] as const;
export type FinanceCalendarScenario = (typeof FINANCE_CALENDAR_SCENARIOS)[number];

export interface FinanceCalendarEventEntity {
  eventId: string;
  scenario: string;
  dateTime: string;
  event: string;
  description: string;
}

/**
 * Transforms FinanceCalendarEventEntity[] into the S3/widget-compatible format.
 * Use this instead of inline `.map()` calls to keep the mapping in one place.
 */
export const toFinanceCalendarS3Format = (events: FinanceCalendarEventEntity[]) =>
  events.map((e) => ({
    scenario: e.scenario,
    date_time: e.dateTime,
    event: e.event,
    description: e.description
  }));

/**
 * Transforms FinanceCalendarEventEntity[] directly into the widget's FinanceCalendarDataTypes[],
 * bypassing the intermediate S3 format. Use this for rendering the calendar widget.
 */
export const toFinanceCalendarWidgetData = (events: FinanceCalendarEventEntity[]): import('./FinanceCalendarWidget').FinanceCalendarDataTypes[] => {
  const dataScenarios = [...new Set(events.map((e) => e.scenario))];
  const allScenarios = [...new Set([...FINANCE_CALENDAR_SCENARIOS, ...dataScenarios])];

  return allScenarios.map((scenario) => {
    const items = events.filter((e) => e.scenario === scenario);
    return {
      scenario,
      table_item: items
        .map((e) => ({
          date: new Date(e.dateTime),
          event: e.event,
          description: e.description
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime())
    };
  });
};
